import {coerceBooleanProperty} from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  OnInit,
  Optional,
  Renderer2,
} from '@angular/core';
import {takeUntilDestroyed, UntilDestroy} from '@atlas-angular/rxjs';
import {CssClassUtility, CssClassUtilityFactory, Utilities} from '@maia/core';

import {CollapsibleRadioGroupFormElementComponent} from '../collapsible-radio-group-form-element/collapsible-radio-group-form-element.component';
import {CommonButton} from '../common-button.interface';

import {
  DEFAULT_RADIO_BUTTON_SIZE,
  RADIO_BUTTON_CLASSES,
  RadioButtonSize,
} from './collapsible-radio-button.utils';

const generateId = Utilities.createIdGenerator('maia-radio-button');

/**
 * @ngModule RadioButtonsModule
 */
@Component({
  selector: 'maia-collapsible-radio-button',
  templateUrl: './collapsible-radio-button.component.html',
  styleUrls: ['./collapsible-radio-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {role: 'radio'},
})
@UntilDestroy()
export class CollapsibleRadioButtonComponent implements CommonButton, OnInit {
  private readonly _cssClassUtility: CssClassUtility<typeof RADIO_BUTTON_CLASSES>;
  private _disabled: boolean;
  public forceDisabled: boolean;
  private _size: RadioButtonSize;

  // The id of the radio button. An id is auto-generated in case no outside id is set. The id is
  // considered final once the component is set-up, and changing it breaks this component's
  // behaviour.
  @Input()
  @HostBinding('id')
  public id = generateId();

  @Input()
  // eslint-disable-next-line
  public value: any;

  public constructor(
    @Optional() private readonly radioGroup: CollapsibleRadioGroupFormElementComponent,
    private readonly _changeDetector: ChangeDetectorRef,
    cssClassUtilityFactory: CssClassUtilityFactory,
    renderer: Renderer2,
    elementRef: ElementRef,
  ) {
    this._cssClassUtility = cssClassUtilityFactory.create(
      RADIO_BUTTON_CLASSES,
      renderer,
      elementRef,
    );
    this.size = DEFAULT_RADIO_BUTTON_SIZE;
  }

  @Input()
  @HostBinding('class.maia-collapsible-radio-button--disabled')
  public get disabled(): boolean {
    return this._disabled || this.forceDisabled;
  }

  public set disabled(disabled: boolean) {
    this._disabled = coerceBooleanProperty(disabled);
  }

  @Input()
  public set size(size: RadioButtonSize) {
    this._size = size;
    this._cssClassUtility.setValue('size', this._size);
  }

  public get size(): RadioButtonSize {
    return this._size;
  }

  @Input()
  @HostBinding('attr.aria-checked')
  public active: boolean;

  @HostListener('click')
  public select(): void {
    if (!this.disabled && this.radioGroup != null) {
      this.radioGroup.selectedValue = this.value;
    }
  }

  private _updateActive(value: any): void {
    const active = this.value === value;

    if (active !== this.active) {
      this.active = active;
      this._changeDetector.detectChanges();
    }

    if (this.active && this.radioGroup != null) {
      this.radioGroup.activeDescendant = this.id;
    }
  }

  public ngOnInit(): void {
    if (this.radioGroup != null) {
      this.radioGroup.selectedValue$
        .pipe(takeUntilDestroyed(this))
        .subscribe((value: any) => this._updateActive(value));
    }
  }
}
